import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createBlock as _createBlock, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "mb-l pb-xs border-gray-200 border-b-2 flex items-center justify-between" }
const _hoisted_2 = {
  key: 0,
  class: "max-h-cardcontent overflow-y-scroll"
}
const _hoisted_3 = {
  key: 0,
  class: "text-sm block"
}
const _hoisted_4 = { class: "flex" }
const _hoisted_5 = { key: 0 }

import RoundedCard from "@/components/RoundedCard.vue";
import {
  encryptCareProviderGroupKeyForCareProviderGroup,
  hexToBytes,
  bytesToHex,
  createKeyPair,
} from "@/helpers/encryption";
import { Profile } from "@/interfaces/profile";
import { computed, ref, onMounted } from "vue";
import { PublicDataRequests } from "@/helpers/requestHelpers/publicDataRequests";
import { CareProviderGroupRequests } from "@/helpers/requestHelpers/careProviderGroupRequests";

import { useCareProviderGroupStore } from "@/store/careProviderGroupStore";
import { usePublicStore } from "@/store/publicStore";
import { useSessionStore } from "@/store/sessionStore";
import { useAccountStore } from "@/store/accountStore";
import UmanaHeadline from "@/components/UmanaHeadline.vue";
import { supabase } from "@/helpers/supabase";
import ListTile from "@/components/ListTile.vue";
import IconButton from "@/components/IconButton.vue";
import CoreIcon from "@/components/CoreIcon.vue";
import DialogCard from "@/components/DialogCard.vue";
import ToolTip from "@/components/ToolTip.vue";
import { useNotificationStore } from "@/store/notificationStore";
import { createEncryptionPassword } from "@/helpers/requestHelpers/profileRequests";
import { PublicProfileKey } from "@/interfaces/publicProfileKey";
import SplittedTabs from "@/components/SplittedTabs.vue";
import TabButton from "@/components/TabButton.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'MembersCard',
  props: {
    isOwnerOfSelectedCareProviderGroup: { type: Boolean }
  },
  setup(__props: any) {

const publicStore = usePublicStore();
const accountStore = useAccountStore();
const sessionStore = useSessionStore();

const careProviderGroupStore = useCareProviderGroupStore();

const connectedProfiles = computed<Profile[] | null>(
  () => careProviderGroupStore.connectedProfiles
);

const confirmNewOwner = ref<Profile | null>(null);
const newAccountModel = ref<any>({});

const shareWithProfile = ref<Profile | null>(null);
const showAddCareProviderToGroup = ref<boolean>(false);
const addNewAccount = ref<boolean>(false);

const selectedCareProviderGroup = computed(
  () => sessionStore.selectedCareProviderGroup
);
const ownerOfSelectedCareProviderGroup = computed(() =>
  (publicStore.profiles ?? []).find(
    (profile) => profile.id == sessionStore.selectedCareProviderGroup?.owner
  )
);

const props = __props;

onMounted(() => {
  careProviderGroupStore.updateConnectedProfiles();
});

const availableAccounts = computed(() => {
  let profiles: { label: string; value: Profile | null }[] = [];
  profiles.push({
    value: null,
    label: "Wählen",
  });
  for (let profile of publicStore.profiles ?? []) {
    if (connectedProfiles.value?.find((p) => p.id == profile.id)) continue;
    profiles.push({
      value: profile,
      label: `${profile.first_name} ${profile.last_name}`,
    });
  }
  return profiles;
});

async function changeOwnerShip(newOwner: Profile | null) {
  if (
    newOwner == null ||
    sessionStore.selectedCareProviderGroup == null ||
    accountStore.careProviderGroupPrivateKeys == null
  ) {
    return;
  }

  await new CareProviderGroupRequests().changeOwnerOfCareProviderGroup(
    sessionStore.selectedCareProviderGroup.id,
    newOwner.id
  );
  confirmNewOwner.value = null;
  await accountStore.loadCpgs();
  sessionStore.selectedCareProviderGroup.owner = newOwner.id;
}

function wait(milliseconds: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
}

async function inviteAccount() {
  await wait(10);
  let profile = shareWithProfile.value;
  if (profile == null || sessionStore.selectedCareProviderGroup == null) {
    return;
  }
  let currentProfileKeyEntry =
    await new PublicDataRequests().currentPublicKeyForProfile(profile.id);
  if (currentProfileKeyEntry == null) {
    return;
  }
  await encryptAndSavePrivateKeys(profile.id, currentProfileKeyEntry);
  await careProviderGroupStore.updateConnectedProfiles();
  showAddCareProviderToGroup.value = false;
}

async function createAndInviteAccount() {
  await wait(10);

  if (!sessionStore.selectedCareProviderGroup) return;

  let password = newAccountModel.value.password;
  let email = newAccountModel.value.email;
  let passwordData = await createEncryptionPassword(password);
  try {
    let res = await supabase.functions.invoke("addUser", {
      body: {
        email: email,
        public_key: passwordData.publicKeyAsHex,
        opslimit: passwordData.opslimit,
        algorithm: passwordData.algorithm,
        memlimit: passwordData.memlimit,
        salt: passwordData.saltAsHex,
      },
    });
    if (res.error) {
      useNotificationStore().notifications.push({
        msg: `Fehler - Der Account konnte nicht erstellt werden. (#mc00b)`,
        type: "danger",
      });
      return;
    }
    if (res.data.errorCode) {
      if (res.data.action == 2) {
        useNotificationStore().notifications.push({
          msg: `Der Account wurde erstellt, konnte aber nicht aktiviert werden. Der Benutzer muss sich einloggen, damit er aktiviert wird. (#mc0${res.data.errorCode})`,
          type: "warning",
        });
        return;
      } else if (res.data.action == 3) {
        useNotificationStore().notifications.push({
          msg: `Der Benutzer wurde erstellt und und zu Ihrem Unternehmen hinzugefügt. Es konnte jedoch keine Bestätigungsmail an den Account versendet werden. (#mc0${res.data.errorCode})`,
          type: "warning",
        });
      } else {
        let detailError = "";
        if (res.data.errorCode == 2) {
          detailError =
            "Diese E-Mail hat bereits einen Account. Bitte laden Sie den vorhandenen Account zum Unternehmen ein.";
        }
        useNotificationStore().notifications.push({
          msg: `Fehler - Der Account konnte nicht erstellt werden. ${detailError} (#mc0${res.data.errorCode})`,
          type: "danger",
        });
        return;
      }
    }
    await encryptAndSavePrivateKeys(res.data.user_id, {
      id: res.data.id,
      public_key: passwordData.publicKeyAsHex,
    });
    newAccountModel.value = {};
    await careProviderGroupStore.updateConnectedProfiles();
    showAddCareProviderToGroup.value = false;
    useNotificationStore().notifications.push({
      msg: `Der Benutzer wurde erstellt und zu Ihrem Unternehmen hinzugefügt. Er kann sich von nun an mit seiner E-Mail einloggen und beim ersten Login sein Benutzerkonto aktivieren. Bitte teilen Sie ihm das Passwort über einen sicheren Kanal mit. Das Passwort sollte aus Sicherheitsgründen nach dem ersten Login geändert werden.`,
      type: "success",
    });
    return;
  } catch (e) {
    useNotificationStore().notifications.push({
      msg: `Fehler - Der Account konnte nicht erstellt werden. (#mc00a)`,
      type: "danger",
    });
    return;
  }
}

async function encryptAndSavePrivateKeys(
  profileId: string,
  currentProfileKeyEntry: PublicProfileKey
) {
  if (!sessionStore.selectedCareProviderGroup) return;
  let privateKeys = sessionStore.selectedCareProviderGroupKeys;
  let encryptedPrivateKeyIds: number[] = [];
  let encryptedPrivateKeys: string[] = [];
  for (let privateKey of privateKeys) {
    let encryptedKey = await encryptCareProviderGroupKeyForCareProviderGroup(
      hexToBytes(currentProfileKeyEntry.public_key),
      privateKey[1].key
    );
    encryptedPrivateKeyIds.push(privateKey[1].keyId);
    encryptedPrivateKeys.push(bytesToHex(encryptedKey));
  }
  await new CareProviderGroupRequests().addToCareProviderGroup(
    sessionStore.selectedCareProviderGroup.id,
    profileId,
    currentProfileKeyEntry.id,
    encryptedPrivateKeyIds,
    encryptedPrivateKeys
  );
}

async function removeConnectedProfile(connectedProfileId: string) {
  await new CareProviderGroupRequests().removeFromCareProviderGroup(
    sessionStore.selectedCareProviderGroup?.id ?? 0,
    connectedProfileId
  );
  await changeKey();
  await careProviderGroupStore.updateConnectedProfiles();
}

async function changeKey() {
  if (selectedCareProviderGroup.value) {
    let newKeyPair = await createKeyPair();

    let profileIds: string[] = [];
    let publicKeyIds: number[] = [];
    let encryptedKeys: string[] = [];
    for (let profile of connectedProfiles.value ?? []) {
      let currentProfileKeyEntry =
        await new PublicDataRequests().currentPublicKeyForProfile(profile.id);
      if (!currentProfileKeyEntry) {
        useNotificationStore().notifications.push({
          msg: `Fehler keine Schlüssel des Empfängers ${profile.first_name} ${profile.last_name} gefunden`,
          type: "danger",
        });
        return;
      }
      let encryptedKey = await encryptCareProviderGroupKeyForCareProviderGroup(
        hexToBytes(currentProfileKeyEntry.public_key),
        newKeyPair.privateKey
      );
      profileIds.push(profile.id);
      publicKeyIds.push(currentProfileKeyEntry.id);
      encryptedKeys.push(bytesToHex(encryptedKey));
    }

    const updateCareProviderGroupKeyResult = await supabase.rpc(
      "update_care_provider_group_key",
      {
        _cpg_id: selectedCareProviderGroup.value.id,
        _profile_ids: profileIds,
        _public_key_ids: publicKeyIds,
        _encrypted_keys: encryptedKeys,
        _new_public_key: bytesToHex(newKeyPair.publicKey),
      }
    );
    if (updateCareProviderGroupKeyResult.error) {
      useNotificationStore().notifications.push({
        msg: `Fehler beim speichern des neuen Schlüssels: ${JSON.stringify(
          updateCareProviderGroupKeyResult.error
        )}`,
        type: "danger",
      });
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (props.isOwnerOfSelectedCareProviderGroup)
      ? (_openBlock(), _createBlock(RoundedCard, { key: 0 }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(UmanaHeadline, { h: "h2" }, {
                default: _withCtx(() => [
                  _createTextVNode("Mitglieder")
                ]),
                _: 1
              }),
              _createVNode(IconButton, {
                icon: "plus",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showAddCareProviderToGroup.value = true))
              })
            ]),
            (connectedProfiles.value != null)
              ? (_openBlock(), _createElementBlock("ul", _hoisted_2, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(connectedProfiles.value, (connectedProfile) => {
                    return (_openBlock(), _createBlock(ListTile, {
                      key: connectedProfile.id,
                      "show-trailing": false
                    }, {
                      main: _withCtx(() => [
                        _createTextVNode(_toDisplayString(connectedProfile.first_name) + " " + _toDisplayString(connectedProfile.last_name), 1),
                        (connectedProfile.id == ownerOfSelectedCareProviderGroup.value?.id)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_3, "Administrator"))
                          : _createCommentVNode("", true)
                      ]),
                      trailing: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                          _createVNode(ToolTip, {
                            position: "topLeft",
                            content: "Als Administrator festlegen"
                          }, {
                            default: _withCtx(() => [
                              (
                  connectedProfile.id != ownerOfSelectedCareProviderGroup.value?.id
                )
                                ? (_openBlock(), _createBlock(CoreIcon, {
                                    key: 0,
                                    icon: "user-change",
                                    class: "mr-xs",
                                    onClick: ($event: any) => (confirmNewOwner.value = connectedProfile)
                                  }, null, 8, ["onClick"]))
                                : _createCommentVNode("", true)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(ToolTip, {
                            position: "topLeft",
                            content: "Entfernen"
                          }, {
                            default: _withCtx(() => [
                              (
                  connectedProfile.id != ownerOfSelectedCareProviderGroup.value?.id
                )
                                ? (_openBlock(), _createBlock(CoreIcon, {
                                    key: 0,
                                    icon: "trash",
                                    onClick: ($event: any) => (removeConnectedProfile(connectedProfile.id))
                                  }, null, 8, ["onClick"]))
                                : _createCommentVNode("", true)
                            ]),
                            _: 2
                          }, 1024)
                        ])
                      ]),
                      _: 2
                    }, 1024))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(DialogCard, {
      color: "gray",
      "is-visible": showAddCareProviderToGroup.value,
      "show-footer": false,
      title: "Mitglied zum Unternehmen hinzufügen",
      onClose: _cache[5] || (_cache[5] = ($event: any) => (showAddCareProviderToGroup.value = false))
    }, {
      default: _withCtx(() => [
        _createVNode(SplittedTabs, null, {
          default: _withCtx(() => [
            _createVNode(TabButton, {
              onClick: _cache[1] || (_cache[1] = ($event: any) => (addNewAccount.value = false)),
              selected: !addNewAccount.value
            }, {
              default: _withCtx(() => [
                _createTextVNode("Vorhandene Person hinzufügen")
              ]),
              _: 1
            }, 8, ["selected"]),
            _createVNode(TabButton, {
              onClick: _cache[2] || (_cache[2] = ($event: any) => (addNewAccount.value = true)),
              selected: addNewAccount.value
            }, {
              default: _withCtx(() => [
                _createTextVNode("Neue Person hinzufügen")
              ]),
              _: 1
            }, 8, ["selected"])
          ]),
          _: 1
        }),
        (!addNewAccount.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(UmanaHeadline, {
                h: "h3",
                class: "mb-s"
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Person einladen")
                ]),
                _: 1
              }),
              (availableAccounts.value.length == 1)
                ? (_openBlock(), _createElementBlock("p", _hoisted_5, " Es existieren aktuell keine Profile, die Sie verknüpfen könnten. "))
                : (_openBlock(), _createBlock(_component_FormKit, {
                    key: 1,
                    type: "form",
                    onSubmit: inviteAccount,
                    label: "Mitglied zum Unternehmen hinzufügen",
                    "submit-label": "Mitglied zum Unternehmen hinzufügen"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_FormKit, {
                        type: "select",
                        modelValue: shareWithProfile.value,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((shareWithProfile).value = $event)),
                        options: availableAccounts.value
                      }, null, 8, ["modelValue", "options"])
                    ]),
                    _: 1
                  }))
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createVNode(UmanaHeadline, {
                h: "h3",
                class: "mb-s"
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Neue Person hinzufügen")
                ]),
                _: 1
              }),
              _createVNode(_component_FormKit, {
                type: "form",
                autocomplete: "off",
                onSubmit: createAndInviteAccount,
                label: "Mitglied zum Unternehmen hinzufügen",
                "submit-label": "Mitglied zum Unternehmen hinzufügen",
                modelValue: newAccountModel.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((newAccountModel).value = $event))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_FormKit, {
                    type: "email",
                    options: availableAccounts.value,
                    validation: "required|email",
                    name: "email",
                    label: "E-Mail-Adresse"
                  }, null, 8, ["options"]),
                  _createVNode(_component_FormKit, {
                    type: "password",
                    label: "Passwort",
                    name: "password",
                    validation: "required|confirm",
                    options: availableAccounts.value
                  }, null, 8, ["options"]),
                  _createVNode(_component_FormKit, {
                    type: "password",
                    name: "password_confirm",
                    label: "Passwort wiederholen",
                    validation: "required|confirm",
                    options: availableAccounts.value
                  }, null, 8, ["options"])
                ]),
                _: 1
              }, 8, ["modelValue"])
            ], 64))
      ]),
      _: 1
    }, 8, ["is-visible"]),
    _createVNode(DialogCard, {
      "is-visible": confirmNewOwner.value != null,
      onClose: _cache[6] || (_cache[6] = ($event: any) => (confirmNewOwner.value = null)),
      title: "Administrator ändern",
      "show-footer": true,
      onSubmit: _cache[7] || (_cache[7] = ($event: any) => (changeOwnerShip(confirmNewOwner.value)))
    }, {
      default: _withCtx(() => [
        _createTextVNode(" Möchten Sie den Administrator zu \"" + _toDisplayString(confirmNewOwner.value?.first_name) + " " + _toDisplayString(confirmNewOwner.value?.last_name) + "\" ändern? Sie werden als Administrator entfernt, können aber weiterhin den Patienten ansehen und verändern. Sie können jedoch die Zugriffe nicht mehr verändern oder den Administrator ändern. ", 1)
      ]),
      _: 1
    }, 8, ["is-visible"])
  ], 64))
}
}

})